(function () {
  'use strict';

  const barSelector = '[data-free-shipping-bar][data-multi-step="true"]';
  const barStates = new WeakMap();

  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (Number.isNaN(number) || number == null) return '0';

    const value = (number / 100).toFixed(precision);
    const parts = value.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const fraction = parts[1] ? `${decimal}${parts[1]}` : '';

    return whole + fraction;
  }

  function formatMoney(cents) {
    const format = (window.theme && window.theme.moneyFormat) || '£{{amount}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);

    if (!match) return formatWithDelimiters(cents, 2, ',', '.');

    let value;

    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }

    const formattedMoney = format.replace(match[0], value);
    const textContainer = document.createElement('span');
    textContainer.innerHTML = formattedMoney;

    return textContainer.textContent || value;
  }

  function interpolate(template, replacements) {
    return Object.keys(replacements).reduce(function (message, token) {
      return message.split(`[${token}]`).join(replacements[token]);
    }, template);
  }

  function getCurrencyRate() {
    const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  function getCartTotal(bar, suppliedTotal) {
    if (Number.isFinite(suppliedTotal)) return suppliedTotal;

    const themeTotal = Number(window.theme && window.theme.cartData && window.theme.cartData.total_price);
    if (Number.isFinite(themeTotal)) return themeTotal;

    const initialTotal = Number(bar.dataset.cartTotal);
    return Number.isFinite(initialTotal) ? initialTotal : 0;
  }

  function getSteps(bar) {
    const rate = getCurrencyRate();
    const uniqueSteps = new Map();

    bar.querySelectorAll('[data-free-shipping-step]').forEach(function (step) {
      const threshold = Number.parseFloat(step.dataset.threshold);
      const reward = (step.dataset.reward || '').trim();

      if (!Number.isFinite(threshold) || threshold <= 0 || !reward) return;

      uniqueSteps.set(threshold, {
        threshold: threshold,
        thresholdInCents: Math.round(threshold * rate * 100),
        reward: reward,
      });
    });

    return Array.from(uniqueSteps.values()).sort(function (a, b) {
      return a.threshold - b.threshold;
    });
  }

  function buildMilestones(state) {
    const finalThreshold = state.steps[state.steps.length - 1].thresholdInCents;
    state.milestones.innerHTML = '';

    state.steps.forEach(function (step, index) {
      const marker = document.createElement('span');
      const position = Math.min(100, Math.max(0, (step.thresholdInCents / finalThreshold) * 100));

      marker.className = 'free-shipping-bar__milestone';
      marker.style.setProperty('--milestone-position', `${position}%`);
      marker.dataset.milestoneIndex = String(index);
      marker.title = step.reward;
      marker.setAttribute('aria-hidden', 'true');
      state.milestones.appendChild(marker);
    });

    state.markers = Array.from(state.milestones.children);
  }

  function render(bar, suppliedTotal) {
    const state = barStates.get(bar);
    if (!state || !state.steps.length || !bar.isConnected) return;

    const cartTotal = getCartTotal(bar, suppliedTotal);
    const finalStep = state.steps[state.steps.length - 1];
    const finalThreshold = finalStep.thresholdInCents;
    const nextIndex = state.steps.findIndex(function (step) {
      return cartTotal < step.thresholdInCents;
    });
    const completedIndex = nextIndex === -1 ? state.steps.length - 1 : nextIndex - 1;
    const nextStep = nextIndex === -1 ? null : state.steps[nextIndex];
    const completedStep = completedIndex >= 0 ? state.steps[completedIndex] : null;
    let message;

    if (!completedStep && nextStep) {
      message = interpolate(state.beforeFirstMessage, {
        amount: formatMoney(Math.max(0, nextStep.thresholdInCents - cartTotal)),
        reward: nextStep.reward,
        next_reward: nextStep.reward,
      });
    } else if (completedStep && nextStep) {
      message = interpolate(state.betweenStepsMessage, {
        amount: formatMoney(Math.max(0, nextStep.thresholdInCents - cartTotal)),
        reward: completedStep.reward,
        next_reward: nextStep.reward,
      });
    } else {
      message = interpolate(state.completeMessage, {
        amount: formatMoney(0),
        reward: finalStep.reward,
        next_reward: finalStep.reward,
      });
    }

    const progress = finalThreshold > 0
      ? Math.min(100, Math.max(0, (cartTotal / finalThreshold) * 100))
      : 100;
    const progressValue = `${progress.toFixed(2)}%`;

    if (state.message.textContent !== message) state.message.textContent = message;
    if (state.progress.style.getPropertyValue('--progress-width') !== progressValue) {
      state.progress.style.setProperty('--progress-width', progressValue);
    }

    state.markers.forEach(function (marker, index) {
      marker.classList.toggle('is-unlocked', index <= completedIndex);
      marker.classList.toggle('is-next', index === nextIndex);
    });

    bar.dataset.isOverThreshold = String(cartTotal >= finalThreshold);
    bar.dataset.isLoaded = 'true';
    bar.dataset.cartTotal = String(cartTotal);
  }

  function scheduleRender(bar, suppliedTotal) {
    const state = barStates.get(bar);
    if (!state) return;

    if (Number.isFinite(suppliedTotal)) state.pendingTotal = suppliedTotal;
    window.clearTimeout(state.renderTimer);
    state.renderTimer = window.setTimeout(function () {
      const total = state.pendingTotal;
      state.pendingTotal = undefined;
      render(bar, total);
    }, 0);
  }

  function initialiseBar(bar) {
    if (barStates.has(bar)) return;

    const steps = getSteps(bar);
    const message = bar.querySelector('.free-shipping-bar__message');
    const progress = bar.querySelector('.free-shipping-bar__bar-progress');
    const milestones = bar.querySelector('[data-free-shipping-milestones]');

    if (!steps.length || !message || !progress || !milestones) return;

    const state = {
      steps: steps,
      message: message,
      progress: progress,
      milestones: milestones,
      markers: [],
      beforeFirstMessage: bar.dataset.beforeFirstMessage || 'Spend [amount] more to unlock [reward]',
      betweenStepsMessage: bar.dataset.betweenStepsMessage || '[reward] unlocked — spend [amount] more to unlock [next_reward]',
      completeMessage: bar.dataset.completeMessage || '[reward] unlocked',
      renderTimer: null,
      pendingTotal: undefined,
      observer: null,
    };

    barStates.set(bar, state);
    buildMilestones(state);
    bar.classList.add('free-shipping-bar--multi-step');

    state.observer = new MutationObserver(function () {
      scheduleRender(bar);
    });
    state.observer.observe(message, { childList: true, characterData: true, subtree: true });
    state.observer.observe(progress, { attributes: true, attributeFilter: ['style'] });

    render(bar);
  }

  function scan(root) {
    if (!(root instanceof Element || root instanceof Document)) return;

    if (root instanceof Element && root.matches(barSelector)) initialiseBar(root);
    root.querySelectorAll(barSelector).forEach(initialiseBar);
  }

  scan(document);

  const documentObserver = new MutationObserver(function (records) {
    records.forEach(function (record) {
      record.addedNodes.forEach(function (node) {
        if (node instanceof Element) scan(node);
      });
    });
  });

  if (document.body) {
    documentObserver.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
      documentObserver.observe(document.body, { childList: true, subtree: true });
    }, { once: true });
  }

  document.addEventListener('cart:updated', function (event) {
    const total = Number(event.detail && event.detail.cart && event.detail.cart.total_price);
    document.querySelectorAll(barSelector).forEach(function (bar) {
      scheduleRender(bar, Number.isFinite(total) ? total : undefined);
    });
  });

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
})();
